import React, { useState, useEffect, useRef } from 'react';
import { useWalletStore } from '../store/walletStore';
import * as api from '../api/vexforge';
import toast from 'react-hot-toast';
import {
  SparklesIcon,
  KeyIcon,
  PaperAirplaneIcon,
  ArrowRightIcon,
  XMarkIcon,
  ShieldCheckIcon,
  Cog6ToothIcon,
} from '@heroicons/react/24/outline';

// ── LLM Provider Configs ──────────────────────────────────────

const PROVIDERS = {
  anthropic: {
    name: 'Claude (Anthropic)',
    placeholder: 'sk-ant-...',
    endpoint: 'https://api.anthropic.com/v1/messages',
    models: ['claude-sonnet-4-20250514', 'claude-haiku-4-20250514'],
    defaultModel: 'claude-sonnet-4-20250514',
  },
  openai: {
    name: 'GPT (OpenAI)',
    placeholder: 'sk-...',
    endpoint: 'https://api.openai.com/v1/chat/completions',
    models: ['gpt-4o', 'gpt-4o-mini'],
    defaultModel: 'gpt-4o-mini',
  },
  xai: {
    name: 'Grok (xAI)',
    placeholder: 'xai-...',
    endpoint: 'https://api.x.ai/v1/chat/completions',
    models: ['grok-3-mini', 'grok-3'],
    defaultModel: 'grok-3-mini',
  },
};

// ── System Prompt ─────────────────────────────────────────────

const SYSTEM_PROMPT = `You are a VIP (Vextaris Intent Program) builder assistant. You help developers create declarative on-chain programs for the Vextaris blockchain.

A VIP is a JSON document that composes native blockchain operations into reusable, parameterized programs. No bytecode, no compiler — just JSON.

SCHEMA:
{
  "namespace": "string (3-32 chars, lowercase alphanumeric + hyphens)",
  "name": "string (lowercase alphanumeric + underscores/hyphens)",
  "version": 1,
  "author": "0x... (will be set by wallet)",
  "description": "What this VIP does",
  "params": [
    { "name": "param_name", "type": "ParamType", "required": true/false, "default_value": null, "description": "..." }
  ],
  "steps": [
    { "id": "step_id", "operation": "OperationName", "args": [["arg_name", "value_or_template"]], "loop_over": null }
  ],
  "returns": [["return_name", "template_expression"]]
}

PARAMETER TYPES: String, Address, Amount, U8, U64, Bool, AddressArray, U64Array, StringArray

TEMPLATE EXPRESSIONS (use in step args):
- \${sender} — address of the user invoking the VIP
- \${params.name} — user-supplied parameter
- \${steps.step_id.result.field} — output from a prior step (field: address, collection, multisig, token_id, amount)
- \${item} — current value in a loop iteration
- \${block.height}, \${block.timestamp} — current block info

ALLOWED OPERATIONS (only these can appear in steps):
Transfer, CreateToken, CreateNftCollection, MintNft, TransferNft, BurnNft,
CreateMultiTokenCollection, DefineTokenType, MintMultiToken, BatchMintMultiToken,
TransferMultiToken, BatchTransferMultiToken, BurnMultiToken, UpdateTokenMetadata,
LockTokenMetadata, CreatePool, AddLiquidity, Swap, CreateMultiSig, ProposeMultiSigTx,
RegisterName, Anchor, Stake, Delegate, CreateStakingPool, StakeToPool, CreateProposal, CreateDAO

OPERATION ARGS (common patterns):
- Transfer: to, token, amount
- CreateNftCollection: name, symbol, base_uri, max_supply, royalty_bps, description, owner
- MintNft: collection, token_id, metadata_uri, recipient
- CreateMultiTokenCollection: name, symbol, base_uri, royalty_bps, description, owner
- DefineTokenType: collection, type_id, name, metadata_uri, max_supply, is_fungible
- MintMultiToken: collection, type_id, to, amount
- CreateMultiSig: signers, threshold, label
- Swap: from_token, to_token, amount_in, min_amount_out
- Anchor: namespace, key, value_hash, metadata_uri
- CreatePool: token_a, token_b, amount_a, amount_b
- Stake: amount, validator_pubkey
- Delegate: validator, amount

SAFETY RULES (your output must follow these):
1. Never hardcode recipient addresses — use \${sender} or \${params.*}
2. Approve operations must have spender as \${params.*}
3. Max 30 steps, max 50 expanded operations (with loops)
4. All template expressions must reference declared params or prior steps
5. Step IDs must be unique, no forward references

RESPONSE FORMAT:
Always respond with ONLY the JSON VIP definition. No explanations, no markdown, no code fences.
If the user's request is unclear, respond with a JSON VIP that includes a "description" field explaining what you assumed.
If the request cannot be expressed as a VIP (e.g., requires operations not in the allowed list), respond with: {"error": "explanation of what cannot be done and why"}`;

// ── API Call Functions ─────────────────────────────────────────

async function callAnthropic(apiKey, model, userMessage) {
  const res = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
      'anthropic-version': '2023-06-01',
      'anthropic-dangerous-direct-browser-access': 'true',
    },
    body: JSON.stringify({
      model,
      max_tokens: 4096,
      system: SYSTEM_PROMPT,
      messages: [{ role: 'user', content: userMessage }],
    }),
  });
  if (!res.ok) {
    const err = await res.text();
    throw new Error(`Anthropic API error: ${err}`);
  }
  const data = await res.json();
  return data.content?.[0]?.text || '';
}

async function callOpenAI(apiKey, model, userMessage, endpoint) {
  const res = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`,
    },
    body: JSON.stringify({
      model,
      messages: [
        { role: 'system', content: SYSTEM_PROMPT },
        { role: 'user', content: userMessage },
      ],
      max_tokens: 4096,
      temperature: 0.3,
    }),
  });
  if (!res.ok) {
    const err = await res.text();
    throw new Error(`API error: ${err}`);
  }
  const data = await res.json();
  return data.choices?.[0]?.message?.content || '';
}

async function callLLM(provider, apiKey, model, message) {
  switch (provider) {
    case 'anthropic':
      return callAnthropic(apiKey, model, message);
    case 'openai':
    case 'xai':
      return callOpenAI(apiKey, model, message, PROVIDERS[provider].endpoint);
    default:
      throw new Error('Unknown provider');
  }
}

// ── Settings Panel ────────────────────────────────────────────

function SettingsPanel({ show, onClose }) {
  const [provider, setProvider] = useState(() => localStorage.getItem('vip_llm_provider') || 'anthropic');
  const [apiKey, setApiKey] = useState(() => localStorage.getItem('vip_llm_key') || '');
  const [model, setModel] = useState(() => localStorage.getItem('vip_llm_model') || '');

  const save = () => {
    localStorage.setItem('vip_llm_provider', provider);
    localStorage.setItem('vip_llm_key', apiKey);
    localStorage.setItem('vip_llm_model', model || PROVIDERS[provider].defaultModel);
    toast.success('LLM settings saved');
    onClose();
  };

  const clear = () => {
    localStorage.removeItem('vip_llm_provider');
    localStorage.removeItem('vip_llm_key');
    localStorage.removeItem('vip_llm_model');
    setApiKey('');
    toast.success('LLM credentials cleared');
    onClose();
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-vextaris-darker border border-gray-800 rounded-2xl p-6 w-full max-w-md mx-4">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-white font-semibold text-lg">LLM Configuration</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><XMarkIcon className="w-5 h-5" /></button>
        </div>

        <div className="space-y-4">
          {/* Provider */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">Provider</label>
            <select
              value={provider}
              onChange={(e) => { setProvider(e.target.value); setModel(''); }}
              className="w-full px-4 py-2 bg-vextaris-dark border border-gray-700 rounded-lg text-white focus:outline-none focus:border-vextaris-purple"
            >
              {Object.entries(PROVIDERS).map(([key, p]) => (
                <option key={key} value={key}>{p.name}</option>
              ))}
            </select>
          </div>

          {/* API Key */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">API Key</label>
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={PROVIDERS[provider].placeholder}
              className="w-full px-4 py-2 bg-vextaris-dark border border-gray-700 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-vextaris-purple font-mono text-sm"
            />
          </div>

          {/* Model */}
          <div>
            <label className="block text-sm text-gray-400 mb-1">Model</label>
            <select
              value={model || PROVIDERS[provider].defaultModel}
              onChange={(e) => setModel(e.target.value)}
              className="w-full px-4 py-2 bg-vextaris-dark border border-gray-700 rounded-lg text-white focus:outline-none focus:border-vextaris-purple"
            >
              {PROVIDERS[provider].models.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>

          {/* Security note */}
          <div className="flex items-start space-x-3 bg-vextaris-dark rounded-lg p-3">
            <KeyIcon className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
            <p className="text-gray-500 text-xs leading-relaxed">
              Your API key is stored in your browser's local storage only. It is never sent to Vextaris servers.
              It is sent directly to your chosen LLM provider's API from your browser.
            </p>
          </div>
        </div>

        <div className="flex justify-between mt-6">
          <button onClick={clear} className="text-sm text-red-400 hover:text-red-300 transition-colors">
            Clear credentials
          </button>
          <button
            onClick={save}
            disabled={!apiKey.trim()}
            className="px-6 py-2 bg-gradient-vextaris text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Chat Message ──────────────────────────────────────────────

function ChatMessage({ message }) {
  if (message.role === 'user') {
    return (
      <div className="flex justify-end mb-4">
        <div className="bg-vextaris-purple/20 border border-vextaris-purple/30 rounded-xl rounded-tr-sm px-4 py-3 max-w-[80%]">
          <p className="text-white text-sm whitespace-pre-wrap">{message.content}</p>
        </div>
      </div>
    );
  }

  // Assistant message
  const isError = message.content?.startsWith('{"error"') || message.error;
  const isJson = !isError && message.content?.trim().startsWith('{');

  return (
    <div className="flex justify-start mb-4">
      <div className="max-w-[90%]">
        <div className="flex items-center space-x-2 mb-1">
          <SparklesIcon className="w-4 h-4 text-vextaris-blue" />
          <span className="text-gray-500 text-xs">{message.model || 'AI Assistant'}</span>
        </div>
        {isError ? (
          <div className="bg-red-500/5 border border-red-500/20 rounded-xl rounded-tl-sm px-4 py-3">
            <p className="text-red-400 text-sm">{message.error || message.content}</p>
          </div>
        ) : isJson ? (
          <div className="bg-vextaris-dark border border-gray-800 rounded-xl rounded-tl-sm overflow-hidden">
            <pre className="px-4 py-3 text-green-400 font-mono text-xs overflow-auto max-h-80">
              {message.formatted || message.content}
            </pre>
            {message.onUse && (
              <div className="border-t border-gray-800 px-4 py-2 flex justify-end">
                <button
                  onClick={message.onUse}
                  className="inline-flex items-center space-x-2 px-4 py-1.5 bg-gradient-vextaris text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity"
                >
                  <ArrowRightIcon className="w-3 h-3" />
                  <span>Use this VIP</span>
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-vextaris-dark border border-gray-800 rounded-xl rounded-tl-sm px-4 py-3">
            <p className="text-gray-300 text-sm whitespace-pre-wrap">{message.content}</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────

export default function VipAssistant({ onVipGenerated }) {
  const { address } = useWalletStore();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const messagesEndRef = useRef(null);

  const hasCredentials = !!localStorage.getItem('vip_llm_key');

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim()) return;
    if (!hasCredentials) {
      setShowSettings(true);
      return;
    }

    const userMsg = input.trim();
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: userMsg }]);
    setLoading(true);

    try {
      const provider = localStorage.getItem('vip_llm_provider') || 'anthropic';
      const apiKey = localStorage.getItem('vip_llm_key');
      const model = localStorage.getItem('vip_llm_model') || PROVIDERS[provider].defaultModel;

      const response = await callLLM(provider, apiKey, model, userMsg);

      // Try to parse as JSON and format
      let formatted = response;
      let parsedJson = null;
      try {
        // Strip markdown code fences if present
        let cleaned = response.trim();
        if (cleaned.startsWith('```')) {
          cleaned = cleaned.replace(/^```(?:json)?\n?/, '').replace(/\n?```$/, '');
        }
        parsedJson = JSON.parse(cleaned);
        // Set author to connected wallet
        if (parsedJson && !parsedJson.error && address) {
          parsedJson.author = address;
        }
        formatted = JSON.stringify(parsedJson, null, 2);
      } catch {
        // Not JSON, display as text
      }

      const onUse = parsedJson && !parsedJson.error ? () => {
        if (onVipGenerated) {
          onVipGenerated(formatted);
        } else {
          // Copy to clipboard
          navigator.clipboard.writeText(formatted);
          toast.success('VIP JSON copied to clipboard — paste in the Register tab');
        }
      } : null;

      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: formatted,
        formatted,
        model: `${PROVIDERS[provider].name} / ${model}`,
        onUse,
      }]);
    } catch (err) {
      setMessages((prev) => [...prev, {
        role: 'assistant',
        error: err.message,
        model: 'Error',
      }]);
    }

    setLoading(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex flex-col h-[600px]">
      <SettingsPanel show={showSettings} onClose={() => setShowSettings(false)} />

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <SparklesIcon className="w-5 h-5 text-vextaris-blue" />
          <div>
            <h3 className="text-white font-medium">AI Assistant</h3>
            <p className="text-gray-500 text-xs">Describe what you want. Your LLM generates the VIP.</p>
          </div>
        </div>
        <button
          onClick={() => setShowSettings(true)}
          className={`inline-flex items-center space-x-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
            hasCredentials
              ? 'bg-green-500/10 text-green-400 border border-green-500/20 hover:bg-green-500/20'
              : 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 hover:bg-yellow-500/20'
          }`}
        >
          <Cog6ToothIcon className="w-4 h-4" />
          <span>{hasCredentials ? 'LLM Connected' : 'Configure LLM'}</span>
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto bg-vextaris-darker border border-gray-800 rounded-xl p-4 mb-4">
        {messages.length === 0 && (
          <div className="text-center py-12">
            <SparklesIcon className="w-10 h-10 text-gray-700 mx-auto mb-4" />
            <p className="text-gray-500 text-sm mb-6">Describe what your VIP should do. Examples:</p>
            <div className="space-y-2 max-w-md mx-auto">
              {[
                'Create an escrow that releases payment when both buyer and seller approve',
                'Airdrop 1000 tokens to a list of addresses',
                'Track a shipment with GPS checkpoints and custody handoffs between 3 parties',
                'Set up a DAO treasury that needs 3-of-5 approval to spend',
              ].map((ex, i) => (
                <button
                  key={i}
                  onClick={() => setInput(ex)}
                  className="w-full text-left px-4 py-2 bg-vextaris-dark border border-gray-800 rounded-lg text-gray-400 text-sm hover:border-gray-700 hover:text-white transition-colors"
                >
                  {ex}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <ChatMessage key={i} message={msg} />
        ))}

        {loading && (
          <div className="flex justify-start mb-4">
            <div className="bg-vextaris-dark border border-gray-800 rounded-xl px-4 py-3">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-vextaris-blue rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 bg-vextaris-blue rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-vextaris-blue rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="flex space-x-3">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={hasCredentials ? 'Describe what your VIP should do...' : 'Configure your LLM first (click button above)'}
          disabled={loading}
          rows={2}
          className="flex-1 px-4 py-3 bg-vextaris-dark border border-gray-700 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-vextaris-purple resize-none disabled:opacity-50"
        />
        <button
          onClick={handleSend}
          disabled={loading || !input.trim()}
          className="px-4 bg-gradient-vextaris text-white rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center"
        >
          <PaperAirplaneIcon className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}
